import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { Route, Switch, useLocation } from 'react-router-dom';

import { Box, GlobalStyles, LinearProgress } from '@mui/material';
import AlertsCDS from '@sharedCR/coreDesignSystem/AlertsCDS';
import AppContext from 'app/AppContext';
import { useAuthorization } from 'app/auth';
import Forbidden403Page from 'app/pages/errors/403/Forbidden403Page';
import PushNotificationSignalRContainer from 'app/pages/pushNotifications/PushNotificationSignalRContainer';
import ChangeDomain from 'app/shared/components/common/ChangeDomain';
import ConfirmationDialog from 'app/shared/components/dialogs/ConfirmationDialog';
import Dialogs from 'app/shared/components/dialogs/Dialogs';
import BrandingFooter from 'app/shared/componentsRestyled/common/BrandingFooter';
import _ from 'lodash';

import useAlerts from 'hooks/useAlerts';
import useBranding from 'hooks/useBranding';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useWindowResize from 'hooks/useWindowResize';
import { getIsMobileLayoutForced, getIsNativePlatform } from 'utils/platform';

import { selectIsTitleCompanyUser, selectMasterCompanyId } from 'store/auth/selectors';
import { getCompanyPropertiesAction, settleCompanyPropertiesAction } from 'store/companies/actions';
import { resetSettingsAction, setCurrentPageCode, setSettingsAction } from 'store/global/actions';
import { getSettings, selectIsDesktop, selectLayoutConfig } from 'store/global/selectors';

import BottomNav from './components/BottomNav';
import Header from './components/Header';
import MenuSidebar from './components/MenuSidebar';
import MobileCallSurface from './components/MobileCallSurface';
import MobileTabPanes from './components/MobileTabPanes';
import MobileTabsProvider from './components/MobileTabsProvider';
import PasskeyEnrollmentHost from './components/PasskeyEnrollmentHost';
import TelephonyHost from './components/TelephonyHost';
import {
  ckEditorDarkStyles,
  contentStyles,
  contentWrapperStyles,
  mobileOverlaySafeAreaStyles,
  mobileTopInsetStyles,
  rootStyles,
  safeAreaTopBandStyles,
} from './DefaultLayout.styles';

const DefaultLayout = () => {
  const dispatch = useDispatch();
  const { messages: alertMessages, hideMessage: hideAlertMessage } = useAlerts();

  // The bottom-nav redesign is for the native (Capacitor) mobile build ONLY — the desktop
  // web app keeps its header + sidebar at every width. `getIsMobileLayoutForced` is a dev/QA
  // opt-in (?mobileNav=1 / localStorage.forceMobileNav='1') to preview it in a browser.
  const isMobileNav = getIsNativePlatform() || getIsMobileLayoutForced();

  const location = useLocation();

  useBranding();

  const { routes } = useContext(AppContext);

  const [windowWidth] = useWindowResize();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const matched = useMemo(
    () => matchRoutes(routes, location.pathname)[0],
    [routes, location.pathname],
  );

  const { current: settings, default: defaultSettings } = useSelector(getSettings);
  const config = useSelector(selectLayoutConfig);
  const isDesktop = useSelector(selectIsDesktop);
  const masterCompanyId = useSelector(selectMasterCompanyId);
  const isTitleCompanyUser = useSelector(selectIsTitleCompanyUser);

  // The mobile chrome (bottom nav + safe-area band + the inset/reserve padding) only belongs on
  // toolbar-bearing pages. The login route runs with `toolbar.display` false, so it stays full-bleed
  // — no BottomNav reserve eating the bottom into a white band, and the fixed carousel owns the frame.
  const showMobileChrome = isMobileNav && !!config?.toolbar?.display;

  // Top safe-area inset is GLOBAL on native (every screen — login included). The bottom-nav inset
  // is reserved per kept-alive tab pane (KeepAlivePane), not on the shared content container.
  const mobileContentStyles = useMemo(() => [contentStyles, mobileTopInsetStyles], []);

  const { isAccessGranted, isResolvingPermissions } = useAuthorization(matched?.route);

  const renderedRoutesWithDialogs = useMemo(
    () =>
      renderRoutes(
        routes.map(route => {
          const Component = route.component;

          return {
            ...route,
            component: props => (
              <>
                <Dialogs />
                <ConfirmationDialog />
                <Component {...props} />
              </>
            ),
          };
        }),
      ),
    [routes],
  );

  const configureRoute = useCallback(() => {
    const currentPageCode = matched?.route?.pageCode;

    dispatch(setCurrentPageCode(currentPageCode));

    if (matched && matched.route.settings) {
      const routeSettings = _.merge({}, defaultSettings, matched.route.settings);

      if (!_.isEqual(settings, routeSettings)) {
        dispatch(setSettingsAction(_.merge({}, routeSettings)));
      }

      return;
    }

    if (!_.isEqual(settings, defaultSettings)) {
      dispatch(resetSettingsAction());
    }
  }, [settings, defaultSettings, dispatch, matched]);

  useEffect(() => {
    configureRoute();
  }, [configureRoute]);

  // Company-property gates (EnableCrm) read from this slice — the CRM nav section and
  // the lead-stage redirect in LoanApplicationPage — so load the master company's
  // properties once per session as soon as the user is authenticated. On failure the
  // rejectedAction marks the fetch settled so the gates fail OPEN to the property
  // defaults (why the sentinel is separate: companyPropertiesSettled.reducer.js).
  useEffectSafeStrictMode(() => {
    // The title/settlement-agent (closing portal) has no company-admin scope, so this 403s for
    // them — and they never read the CRM/lead gates it feeds. Skip it for that role.
    if (!masterCompanyId || isTitleCompanyUser) {
      return;
    }

    dispatch(
      getCompanyPropertiesAction({
        companyId: masterCompanyId,
        silent: true,
        rejectedAction: () => settleCompanyPropertiesAction(masterCompanyId),
      }),
    ).catch(Error);
  }, [dispatch, masterCompanyId, isTitleCompanyUser]);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    setIsSidebarOpen(false);
  }, [isDesktop]);

  // When the desktop header is hidden (native/mobile nav), expose its height as 0 so every overlay
  // that offsets by `var(--app-header-height, ...)` (drawers, side panels) anchors to the top
  // instead of leaving a header-sized gap. Set on the root element so portaled overlays inherit it.
  useEffect(() => {
    const root = document.documentElement;

    if (isMobileNav) {
      root.style.setProperty('--app-header-height', '0px');
    }

    return () => root.style.removeProperty('--app-header-height');
  }, [isMobileNav]);

  const toggleIsSidebarOpen = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen]);

  return (
    <PushNotificationSignalRContainer>
      <ChangeDomain />
      <PasskeyEnrollmentHost />
      <AlertsCDS messages={alertMessages} onClose={hideAlertMessage} />

      <TelephonyHost>
        <Box sx={rootStyles}>
          <GlobalStyles styles={ckEditorDarkStyles} />
          {isMobileNav && <GlobalStyles styles={mobileOverlaySafeAreaStyles} />}

          {isMobileNav && <MobileCallSurface />}

          {isMobileNav && <Box sx={safeAreaTopBandStyles} />}

          <Box sx={contentWrapperStyles}>
            {config?.toolbar?.display && !isMobileNav && (
              <>
                <Header
                  isSidebarOpen={isSidebarOpen}
                  toggleIsSidebarOpen={toggleIsSidebarOpen}
                  windowWidth={windowWidth}
                />

                <MenuSidebar isSidebarOpen={isSidebarOpen} onClose={toggleIsSidebarOpen} />
              </>
            )}

            {isMobileNav ? (
              <MobileTabsProvider>
                <Box sx={mobileContentStyles}>
                  <Box id="loader-root" />
                  <Suspense fallback={<LinearProgress className="visible" color="primary" />}>
                    <MobileTabPanes
                      isAccessGranted={isAccessGranted}
                      isResolvingPermissions={isResolvingPermissions}
                      reserveBottom={showMobileChrome}
                      routes={routes}
                    />
                  </Suspense>
                </Box>

                {showMobileChrome && <BottomNav />}
              </MobileTabsProvider>
            ) : (
              <>
                <Box sx={contentStyles}>
                  <Box id="loader-root" />
                  <Suspense fallback={<LinearProgress className="visible" color="primary" />}>
                    {isResolvingPermissions ? (
                      <LinearProgress className="visible" color="primary" />
                    ) : (
                      <Switch>
                        {!isAccessGranted && (
                          <Route
                            component={Forbidden403Page}
                            exact={matched?.route.exact}
                            path={matched?.route.path}
                          />
                        )}
                        {renderedRoutesWithDialogs}
                      </Switch>
                    )}
                  </Suspense>
                </Box>

                {config?.toolbar?.display && <BrandingFooter />}
              </>
            )}
          </Box>
        </Box>
      </TelephonyHost>
    </PushNotificationSignalRContainer>
  );
};

export default DefaultLayout;
